import { EventEmitter, Injectable, Output } from '@angular/core';
import { SessionStorageService } from 'ngx-webstorage';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import Account from 'app/core/user/account.model';
import AppConfigService from '../app-config.service';

@Injectable()
export default class AccountService {
  @Output() changeUserName: EventEmitter<any> = new EventEmitter<any>();

  private userIdentity: any;

  private authenticated = false;

  private authenticationState = new Subject<any>();

  public slideScreen = new BehaviorSubject<any>(false);

  public maintenanceScreen = new BehaviorSubject<any>(false);

  public isMaintenance = new BehaviorSubject<any>({});

  public userNameBySubject = new BehaviorSubject<any>('');

  constructor(private sessionStorage: SessionStorageService, private http: HttpClient) {}

  fetch(): Observable<HttpResponse<Account>> {
    return this.http.get<Account>(`${AppConfigService.envConfig.fssServicesBaseUrl}api/account`, { observe: 'response' });
  }

  save(account: any): Observable<HttpResponse<any>> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/account`, account, { observe: 'response' });
  }

  authenticate(identity) {
    this.userIdentity = identity;
    this.authenticated = identity !== null;
    this.authenticationState.next(this.userIdentity);
  }

  hasAnyAuthority(authorities: string[]): boolean {
    if (!this.authenticated || !this.userIdentity || !this.userIdentity.authorities) {
      return false;
    }

    return authorities.some((authority: string) => this.userIdentity.authorities.includes(authority));
  }

  identity(force?: boolean): Promise<Account> {
    if (force) {
      this.userIdentity = undefined;
    }

    // check and see if we have retrieved the userIdentity data from the server.
    // if we have, reuse it by immediately resolving
    if (this.userIdentity) {
      return Promise.resolve(this.userIdentity);
    }

    if (this.sessionStorage.retrieve('authenticationToken') !== null) {
      // retrieve the userIdentity data from the server, update the identity object, and then resolve.
      return this.fetch().toPromise()
        .then((response) => {
          const account: Account = response.body;
          if (account) {
            this.userIdentity = account;
            this.authenticated = true;
          } else {
            this.userIdentity = null;
            this.authenticated = false;
          }
          this.authenticationState.next(this.userIdentity);
          return this.userIdentity;
        })
        .catch(() => {
          this.userIdentity = null;
          this.authenticated = false;
          this.authenticationState.next(this.userIdentity);
          this.sessionStorage.clear('displayName');
          this.changeUserName.emit('');
          return null;
        });
    }
    this.userIdentity = null;
    this.authenticated = false;
    return Promise.resolve(this.userIdentity);
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  getAuthenticationState(): Observable<any> {
    return this.authenticationState.asObservable();
  }

  assistForgotUsername(info: any): Observable<any> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile/forgotUserName`, info);
  }

  isSystemMaintenanceModeOn() {
    const maintenanceModeUrl = `${AppConfigService.envConfig.fssServicesBaseUrl}api/notification/wallnotification`;
    return this.http.get(maintenanceModeUrl);
  }

  getEmitter() {
    return this.changeUserName;
  }
}
